import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "sonner";

/**
 * Parse the hash params ONCE at module-load time.
 *
 *  - Runs before BrowserRouter mounts (React Router v6 calls
 *    history.replaceState on init which strips the hash).
 *  - Survives StrictMode double-mount because module-level code only runs
 *    once per page load.
 *  - Captures both `window.__fgInitialHash` (captured even earlier in
 *    index.js) AND `window.location.hash` (fallback).
 */
const HASH_PAYLOAD = (() => {
  if (typeof window === "undefined") return { sessionId: "", token: "", error: "" };
  const raw =
    window.__fgInitialHash ||
    window.location.hash ||
    "";
  // Also clear the captured value so a subsequent client-side navigation
  // back to this page (e.g. after error) doesn't replay an old fragment.
  window.__fgInitialHash = "";
  const params = new URLSearchParams(raw.replace(/^#/, ""));
  // Wipe the visible fragment so token / session_id never leaks via share /
  // back-button history.
  try {
    if (raw) window.history.replaceState({}, "", window.location.pathname);
  } catch { /* ignore */ }
  return {
    sessionId: params.get("session_id") || "",
    token: params.get("token") || "",
    error: params.get("error") || "",
  };
})();

/**
 * Lands here after a social-login round-trip.
 *
 * Two flow shapes converge here:
 *
 *  1. Google (Emergent-managed): URL fragment carries `#session_id=<uuid>`.
 *     We POST it to /api/auth/social/google/session, which returns the user
 *     object + access token + sets HTTP-only cookies.
 *
 *  2. Apple / Microsoft / Yahoo: backend has already set cookies + populated
 *     the URL fragment with `#token=<jwt>`. We just call /api/auth/me to
 *     finalise the session.
 *
 *  Error case: URL fragment carries `#error=<code>` → friendly toast + send
 *  the user back to login.
 */
// Module-level guard — ensure the side-effects run exactly once even when
// StrictMode double-mounts the component in development.
let CONSUMED = false;

export default function SocialFinishPage() {
  const navigate = useNavigate();
  const { loginSocial } = useAuth();
  const [status, setStatus] = useState("verifying");
  const [errorMsg, setErrorMsg] = useState("");

  useEffect(() => {
    const finish = async () => {
      if (CONSUMED) return;
      CONSUMED = true;
      try {
        const { sessionId, token, error: errorCode } = HASH_PAYLOAD;
        const returnTo = sessionStorage.getItem("fg_post_social_return") || "/dashboard";
        sessionStorage.removeItem("fg_post_social_return");

        if (errorCode) {
          throw new Error(prettyError(errorCode));
        }

        if (sessionId) {
          let data;
          try {
            const resp = await api.post("/auth/social/google/session", { session_id: sessionId });
            data = resp.data;
          } catch (e) {
            const detail = formatApiErrorDetail(e?.response?.data?.detail);
            throw new Error(detail || "Google sign-in didn't complete — please try again.");
          }
          const res = await loginSocial({
            accessToken: data?.access_token,
            user: data?.user || null,
            isNew: !!data?.is_new_user,
          });
          if (!res.ok) throw new Error(res.error || "Sign-in failed.");
          toast.success(
            data?.is_new_user
              ? `Welcome to Feel Greats, ${data?.user?.name || "there"}!`
              : `Welcome back, ${data?.user?.name || "there"}!`,
          );
          setStatus("done");
          navigate(returnTo === "/login" ? "/dashboard" : returnTo, { replace: true });
          return;
        }

        if (token) {
          const res = await loginSocial({ accessToken: token, user: null });
          if (!res.ok) throw new Error(res.error || "Sign-in failed.");
          toast.success("Signed in.");
          setStatus("done");
          navigate(returnTo === "/login" ? "/dashboard" : returnTo, { replace: true });
          return;
        }

        throw new Error("Missing sign-in token — please try again.");
      } catch (e) {
        setErrorMsg(e?.message || "Sign-in failed.");
        setStatus("error");
        toast.error(e?.message || "Sign-in failed.");
        setTimeout(() => navigate("/login", { replace: true }), 2200);
      }
    };
    finish();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <main className="min-h-[calc(100vh-4rem)] bg-leaf-pattern flex items-center justify-center px-4 py-12" data-testid="social-finish-page">
      <div className="text-center max-w-sm">
        <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-emerald-700 text-white mb-4 mx-auto">
          <span className="text-2xl">{status === "error" ? "!" : "✓"}</span>
        </div>
        <h1 className="font-display text-2xl text-emerald-950 font-light">
          {status === "verifying" ? "Signing you in…" : status === "done" ? "You're in" : "Sign-in issue"}
        </h1>
        <p className="text-emerald-900/65 mt-2 text-sm leading-relaxed">
          {status === "verifying" && "Wrapping things up — this only takes a moment."}
          {status === "done" && "Taking you to your dashboard."}
          {status === "error" && (errorMsg || "Something didn't quite work. We'll take you back to sign in.")}
        </p>
      </div>
    </main>
  );
}

function prettyError(code) {
  switch (code) {
    case "state_mismatch":
      return "Security check failed — please try signing in again.";
    case "provider_disabled":
      return "This sign-in option isn't available right now.";
    case "no_code":
      return "Sign-in was cancelled.";
    default:
      return decodeURIComponent(code || "Sign-in failed.");
  }
}
